/**
 * Media Selector (E20-1194)
 *
 * Full-bleed image selector. A vertical ARIA tablist of titles sits over a stack
 * of crossfading image layers (the tablist's single tabpanel). Selecting a tab —
 * by click or by keyboard (Up/Down/Left/Right, Home/End; automatic activation) —
 * crossfades to that item's image, moves the active state, and reveals its
 * sub-copy (the reveal is pure CSS off aria-selected). Selection is user-driven
 * only; there is no auto-advance. Re-selecting the active tab is a no-op.
 *
 * The crossfade is a CSS opacity transition, so prefers-reduced-motion (handled in
 * CSS) makes it an instant swap. This element is desktop behaviour only — mobile
 * renders separate stacked blocks with no interactivity. Listeners are cleaned up
 * via AbortController.
 */
class MediaSelector extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[role="tablist"]');
    if (!this.list) return;

    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.layers = Array.from(this.querySelectorAll('[data-layer]'));
    this.panel = this.querySelector('[role="tabpanel"]');
    if (this.tabs.length === 0) return;

    this.current = Math.max(
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'),
      0
    );

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i), { signal });
    });
    this.list.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
  }

  // Activate a tab: swap the crossfade layer, move tablist state, relabel the panel.
  select(index, { focus = false } = {}) {
    if (index === this.current || index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.layers.forEach((layer, i) => {
      layer.classList.toggle('is-active', i === index);
    });

    if (this.panel) {
      this.panel.setAttribute('aria-labelledby', this.tabs[index].id);
    }

    if (focus) this.tabs[index].focus();
    this.current = index;
  }

  // Vertical tablist keyboard model (also accepts Left/Right); automatic activation.
  onKeydown(e) {
    const last = this.tabs.length - 1;
    let target;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        target = this.current === last ? 0 : this.current + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        target = this.current === 0 ? last : this.current - 1;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = last;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.select(target, { focus: true });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('media-selector')) {
  customElements.define('media-selector', MediaSelector);
}
